/** @jsx jsx */
// External libraries
import { jsx } from '@emotion/core';
import React, { useState } from 'react';

// Internal modules
import { useStoreActions, useStoreState } from '@/store';
import { CURRENT_YEAR } from './constants';
import { useMountLogging } from '@/lib/reactHooks';
import { features, getContent, getLocale } from '@/lib';
import { INTERSTITIAL_PAYLOAD, logger } from '@/lib/logger';

import { LanguageList } from '../Locale';
import { Link, StyledText } from '../Common';
import { HiddenElementA11y } from '@/components/Common/StyledComponents';
import {
  EntityMessage,
  FooterLegalLinks,
  LazyCountryChangeInterstitial,
  PrivacyAndSecurityMessage,
} from './Components';
import {
  ChangeCountry,
  CountryLanguageContainer,
  FooterLinks,
  LinksContainer,
  StyledFooter,
} from './StyledComponents';

import { FONT_SIZE_SMALL, FONT_WEIGHT_NORMAL_PP } from '@/styles';
import { handleReturnToMerchantLinkClick } from './utils';

const loadCountryChangeInterstitial = () => {
  LazyCountryChangeInterstitial.preload();
};

export const Footer = (): JSX.Element => {
  useMountLogging('Footer');

  const [isCountryChangeInterstitialOpen, setIsCountryChangeInterstitialOpen] =
    useState(false);
  const isFormSubmitting = useStoreState(({ ui }) => ui.isFormSubmitting);
  const cancelUrl = useStoreState(({ api }) => api.cancelUrl);

  const intent = useStoreState(({ api }) => api.cartDetails.intent);

  const toggleIsPageLoading = useStoreActions(
    ({ ui }) => ui.toggleIsPageLoading,
  );

  const { country, language, locale } = getLocale();
  const territoriesByRegion = useStoreState(
    ({ api }) => api.griffinMetadata.territoriesByRegion[locale],
  );
  const countriesList = useStoreState(({ api }) =>
    locale ? api.griffinMetadata.territories[locale] : [],
  );

  const countryText = countriesList?.find(c => c.key === country)?.text;
  const voiceOverText = getContent('accessibility.countrySelector.alt', {
    country: countryText,
  });

  const shouldBlockDomesticPayer = features.isActive('blockNonDomesticPayer');
  const { enabledLanguages } = features.config;

  const handleReturnLinkClick = (e: React.MouseEvent): void => {
    e.preventDefault();
    handleReturnToMerchantLinkClick(
      e,
      toggleIsPageLoading,
      isFormSubmitting,
      intent,
      cancelUrl,
    );
  };

  const toggleCountryChangeInterstitial = (): void => {
    // We need to wait until the territories metadata has been
    // fetched from the API before we open the interstitial,
    // otherwise we won't have the country list.
    if (
      !territoriesByRegion.AMERICAS.length ||
      isFormSubmitting ||
      shouldBlockDomesticPayer
    ) {
      return;
    }

    const action = isCountryChangeInterstitialOpen ? 'close' : 'open';
    const payload = INTERSTITIAL_PAYLOAD({ action, name: 'country_change' });

    logger.fpti(`weasley_${payload.transition_name}`, payload);
    setIsCountryChangeInterstitialOpen(!isCountryChangeInterstitialOpen);
  };

  return (
    <div data-testid="footer">
      <StyledFooter>
        {Boolean(cancelUrl) && (
          <Link
            fontSize={FONT_SIZE_SMALL}
            href="#"
            onClick={handleReturnLinkClick}
            text={getContent('billing.cancelAndRTM')}
          />
        )}

        <CountryLanguageContainer>
          {enabledLanguages.length && <LanguageList currLanguage={language} />}
          <ChangeCountry
            country={country}
            data-testid="footer-change-country"
            disabled={shouldBlockDomesticPayer}
            onClick={toggleCountryChangeInterstitial}
            onFocus={loadCountryChangeInterstitial}
            onMouseOver={loadCountryChangeInterstitial}
          >
            {countryText && (
              <HiddenElementA11y>{voiceOverText}</HiddenElementA11y>
            )}
          </ChangeCountry>
        </CountryLanguageContainer>

        <FooterLinks>
          <LinksContainer>
            <FooterLegalLinks />
          </LinksContainer>

          <StyledText
            fontSize={FONT_SIZE_SMALL}
            fontWeight={FONT_WEIGHT_NORMAL_PP}
          >
            {getContent('footer.copyright', { year: CURRENT_YEAR })}
          </StyledText>
        </FooterLinks>

        {features.isActive('hasFooterPrivacyAndSecurityMessage') && (
          <PrivacyAndSecurityMessage />
        )}

        {features.isActive('hasFooterEntityMessage') && <EntityMessage />}
      </StyledFooter>

      {isCountryChangeInterstitialOpen && (
        <LazyCountryChangeInterstitial
          handleClose={toggleCountryChangeInterstitial}
          territoriesByRegion={territoriesByRegion}
        />
      )}
    </div>
  );
};
